import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AccToEntrataMigrationParams,
  downloadMigrationCsvTemplate,
  EntrataUser,
  executeAccToEntrataMigration,
  isCsvFormatError,
  LoadAllEntrataUsers,
  migrateAccToTenantKey,
  MigrationPreviewResult,
  previewAccToEntrataMigration,
} from "../../services/IntegrationApi";
import {
  GetPossibleMigrations,
  LoadAllPortfolios,
  PortfolioNetworkMigrationRequest,
  PossibleMigration,
  Portfolio,
  RunPortfolioNetworkMigration,
  RunPortfolioNetworkMigrationWithFile,
} from "../../services/PortfolioApi";
import { extractErrorMessage } from "../../helpers/extractErrorMessage";
import { useSnackbar } from "../../hooks/useSnackbar";
import useAuth from "../../routes/login/AuthContext";
import { getMigrationFlowKind, resolveFromType } from "./types";

export type WizardStep = 0 | 1 | 2;
export type LegacyFieldValue = string | number | File | null;

const MAX_CSV_BYTES = 10 * 1024 * 1024; // 10MB

interface UseImportMigrationWizardArgs {
  l1Id: number;
  currentPortfolioId: string;
  importType: string | null | undefined;
  isConnect: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

export const useImportMigrationWizard = ({
  l1Id,
  currentPortfolioId,
  importType,
  isConnect,
  isOpen,
  onClose,
  onSuccess,
}: UseImportMigrationWizardArgs) => {
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();

  const fromType = useMemo(() => resolveFromType(importType), [importType]);

  const [step, setStep] = useState<WizardStep>(0);
  const [targetType, setTargetType] = useState<string>("");
  const [portfolioId, setPortfolioId] = useState<string>("");
  const [entrataUserId, setEntrataUserId] = useState<number | "">("");
  const [entrataPropertyId, setEntrataPropertyId] = useState<string>("");
  const [file, setFile] = useState<File | undefined>();
  const [csvError, setCsvError] = useState<string | null>(null);
  const [legacyFields, setLegacyFields] = useState<Record<string, LegacyFieldValue>>({});
  const [possibleMigrations, setPossibleMigrations] = useState<PossibleMigration[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [entrataUsers, setEntrataUsers] = useState<EntrataUser[]>([]);

  const [optionsLoading, setOptionsLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [legacyMigrating, setLegacyMigrating] = useState(false);

  const [preview, setPreview] = useState<MigrationPreviewResult | null>(null);

  const reset = useCallback(() => {
    setStep(0);
    setTargetType("");
    setPortfolioId("");
    setEntrataUserId("");
    setEntrataPropertyId("");
    setFile(undefined);
    setCsvError(null);
    setLegacyFields({});
    setPreview(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    const load = async () => {
      try {
        setOptionsLoading(true);
        const token = await getAuthToken();
        const [migrationsRes, portfolioRes, entrataRes] = await Promise.all([
          GetPossibleMigrations(currentPortfolioId, l1Id, token),
          LoadAllPortfolios(0, token),
          LoadAllEntrataUsers(token),
        ]);
        setPossibleMigrations(Array.isArray(migrationsRes) ? migrationsRes : []);
        setPortfolios(portfolioRes?.content ?? []);
        setEntrataUsers(entrataRes ?? []);
      } catch {
        showErrorSnackbar("Failed to load migration options");
      } finally {
        setOptionsLoading(false);
      }
    };
    load();
  }, [isOpen, l1Id, currentPortfolioId]);

  const targetOptions = useMemo(() => {
    const targets = possibleMigrations
      .filter((m) => m.origin === importType || m.origin === null)
      .map((m) => m.target);
    return Array.from(new Set(targets));
  }, [possibleMigrations, importType]);

  // Auto-select when only one option
  useEffect(() => {
    if (targetType === "" && targetOptions.length === 1) {
      setTargetType(targetOptions[0]);
    }
  }, [targetOptions, targetType]);

  const selectedMigration = useMemo(() => {
    if (!targetType) return undefined;
    return (
      possibleMigrations.find((m) => m.target === targetType && m.origin === importType) ||
      possibleMigrations.find((m) => m.target === targetType)
    );
  }, [possibleMigrations, importType, targetType]);

  const flowKind = useMemo(
    () => getMigrationFlowKind(importType, targetType),
    [importType, targetType]
  );

  // Reset flow-specific state when the selected target (and thus flow) changes
  useEffect(() => {
    setLegacyFields({});
    setPortfolioId("");
    setEntrataUserId("");
    setEntrataPropertyId("");
    setFile(undefined);
    setCsvError(null);
    setPreview(null);
    setStep(0);
  }, [flowKind, targetType]);

  const setLegacyField = useCallback((name: string, value: LegacyFieldValue) => {
    setLegacyFields((prev) => ({ ...prev, [name]: value }));
  }, []);

  const legacyIsValid = useMemo(() => {
    if (!selectedMigration) return false;
    const fields = selectedMigration.fields ?? [];
    if (fields.length === 0) return true;
    return fields.every((field) => {
      const value = legacyFields[field.fieldName];
      if (value === undefined || value === null || value === "") return false;
      const type = field.fieldType.toLowerCase();
      if (type === "number") {
        const n = Number(String(value));
        return !Number.isNaN(n);
      }
      if (type === "string") {
        return String(value).trim() !== "";
      }
      if (type === "file") {
        return value instanceof File;
      }
      return true;
    });
  }, [selectedMigration, legacyFields]);

  const buildParams = useCallback((): AccToEntrataMigrationParams | null => {
    const parsedPropertyId = Number(entrataPropertyId);
    if (
      !l1Id ||
      !portfolioId ||
      entrataUserId === "" ||
      !entrataPropertyId ||
      Number.isNaN(parsedPropertyId)
    ) {
      return null;
    }
    return {
      l1Id,
      fromType,
      portfolioId,
      entrataUserId: Number(entrataUserId),
      entrataPropertyId: parsedPropertyId,
    };
  }, [l1Id, fromType, portfolioId, entrataUserId, entrataPropertyId]);

  const isConfigComplete = buildParams() !== null;

  const downloadTemplate = useCallback(async () => {
    if (!l1Id) return;
    try {
      setDownloadingTemplate(true);
      const { blob, fileName } = await downloadMigrationCsvTemplate(l1Id, await getAuthToken());
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = (fileName || `network_${l1Id}_migration_template.csv`).replace(/"/g, "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      showErrorSnackbar("Failed to download CSV template");
    } finally {
      setDownloadingTemplate(false);
    }
  }, [l1Id]);

  const goToUpload = useCallback(() => {
    if (!targetType) {
      showErrorSnackbar("Please select a migration target");
      return;
    }
    if (!buildParams()) {
      showErrorSnackbar("Please fill in portfolio, Entrata connection and Entrata property ID");
      return;
    }
    setStep(1);
  }, [buildParams, targetType]);

  const runPreview = useCallback(async () => {
    const params = buildParams();
    if (!params || !file) {
      showErrorSnackbar("Please upload a CSV file");
      return;
    }
    try {
      setPreviewing(true);
      const res = await previewAccToEntrataMigration(params, file, await getAuthToken());
      const body = res?.parsedBody as MigrationPreviewResult | undefined;
      if (!res?.ok || !body) {
        showErrorSnackbar("Validation failed. Please check the uploaded file.");
        return;
      }
      setPreview(body);
      setStep(2);
    } catch {
      showErrorSnackbar("Validation request failed");
    } finally {
      setPreviewing(false);
    }
  }, [buildParams, file]);

  const runMigration = useCallback(async () => {
    const params = buildParams();
    if (!params || !file) {
      showErrorSnackbar("Missing data; please restart the wizard");
      return;
    }
    try {
      setExecuting(true);
      const res = await executeAccToEntrataMigration(params, file, await getAuthToken());
      if (!res?.ok) {
        showErrorSnackbar("Migration failed");
        return;
      }
      showSuccessSnackbar("Migration completed");
      if (onSuccess) await onSuccess();
      onClose();
    } catch {
      showErrorSnackbar("Migration failed");
    } finally {
      setExecuting(false);
    }
  }, [buildParams, file, onSuccess, onClose]);

  const runLegacyMigration = useCallback(async () => {
    if (!selectedMigration) return;
    if (!legacyIsValid) {
      showErrorSnackbar("Please fill in all required fields");
      return;
    }
    try {
      setLegacyMigrating(true);
      const token = await getAuthToken();

      const fileField = selectedMigration.fields.find(
        (f) => f.fieldType.toLowerCase() === "file"
      );
      const fileValue = fileField ? legacyFields[fileField.fieldName] : null;
      const hasFile = fileField && fileValue instanceof File;

      let result;
      if (hasFile && fileValue instanceof File) {
        result = await RunPortfolioNetworkMigrationWithFile(
          l1Id,
          importType ?? "",
          targetType,
          fileValue,
          token
        );
      } else {
        const fields: Record<string, any> = {};
        for (const f of selectedMigration.fields) {
          const v = legacyFields[f.fieldName];
          if (v === undefined || v === null) continue;
          const type = f.fieldType.toLowerCase();
          if (type === "number") fields[f.fieldName] = Number(String(v));
          else if (type === "file") fields[f.fieldName] = v;
          else fields[f.fieldName] = String(v);
        }
        const body: PortfolioNetworkMigrationRequest = { target: targetType, fields };
        result = await RunPortfolioNetworkMigration(currentPortfolioId, l1Id, body, token);
      }

      if (!result?.ok) {
        showErrorSnackbar("Migration failed");
        return;
      }
      showSuccessSnackbar("Migration completed");
      if (onSuccess) await onSuccess();
      onClose();
    } catch {
      showErrorSnackbar("Migration failed");
    } finally {
      setLegacyMigrating(false);
    }
  }, [
    selectedMigration,
    legacyIsValid,
    legacyFields,
    l1Id,
    importType,
    targetType,
    currentPortfolioId,
    onSuccess,
    onClose,
  ]);

  // --- ACC → TenantKey flow (optional CSV) ---

  const selectCsvFile = useCallback((selected: File) => {
    if (!selected.name.toLowerCase().endsWith(".csv")) {
      setCsvError("Only .csv files are accepted.");
      return;
    }
    if (selected.size > MAX_CSV_BYTES) {
      setCsvError("File is too large. The maximum size is 10MB.");
      return;
    }
    setCsvError(null);
    setFile(selected);
  }, []);

  const clearCsvFile = useCallback(() => {
    setFile(undefined);
    setCsvError(null);
  }, []);

  const runTenantKeyMigration = useCallback(async () => {
    try {
      setExecuting(true);
      const res = await migrateAccToTenantKey(l1Id, currentPortfolioId, file, await getAuthToken());
      if (!res?.ok) {
        if (isCsvFormatError(res)) {
          setCsvError(
            "The uploaded CSV could not be processed. Check the required columns and values, then upload it again."
          );
          setStep(1);
        } else {
          showErrorSnackbar(extractErrorMessage(res, "Migration failed"));
        }
        return;
      }
      showSuccessSnackbar("Migration completed");
      if (onSuccess) await onSuccess();
      onClose();
    } catch {
      showErrorSnackbar("Migration failed");
    } finally {
      setExecuting(false);
    }
  }, [l1Id, currentPortfolioId, file, onSuccess, onClose]);

  return {
    step,
    setStep,
    fromType,

    targetType,
    setTargetType,
    targetOptions,
    selectedMigration,
    flowKind,
    isConnect,

    // entrata-flow form
    portfolioId,
    setPortfolioId,
    entrataUserId,
    setEntrataUserId,
    entrataPropertyId,
    setEntrataPropertyId,
    file,
    setFile,
    isConfigComplete,

    // tenantkey-flow CSV
    csvError,
    selectCsvFile,
    clearCsvFile,

    // legacy form
    legacyFields,
    setLegacyField,
    legacyIsValid,

    // lookups
    portfolios,
    entrataUsers,
    optionsLoading,

    // preview
    preview,

    // loading flags
    downloadingTemplate,
    previewing,
    executing,
    legacyMigrating,

    // actions
    downloadTemplate,
    goToUpload,
    runPreview,
    runMigration,
    runLegacyMigration,
    runTenantKeyMigration,
  };
};

export type ImportMigrationWizardState = ReturnType<typeof useImportMigrationWizard>;
